import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import './UserAvatar.css';

export const UserAvatar = ({
                               imageUrl,
                               userName = '?',
                               size = 35,
                               bgColor,
                               textColor,
                               font,
                               numChars = 1,
                               showNameOnHover = true
                           }) => {
    const [imageError, setImageError] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
    const avatarRef = useRef(null);

    const handleMouseEnter = () => {
        if (avatarRef.current) {
            const rect = avatarRef.current.getBoundingClientRect();
            setTooltipPos({
                // Open downward and LEFT-ALIGNED to the avatar, matching the data-grid tag/expression
                // tooltips (HoverTooltip) — they anchor to the trigger's left edge, not its center.
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
            });
        }
        setHovered(true);
    };

    const handleImageError = () => {
        setImageError(true);
    };

    const getInitials = () => {
        if (!userName || typeof userName !== 'string' || userName.length === 0) {
            return '?';
        }

        const words = userName.trim().split(/\s+/);

        if (numChars === 1) {
            return userName.charAt(0).toUpperCase();
        }

        if (words.length >= numChars) {
            return words
                .slice(0, numChars)
                .map(word => word.charAt(0).toUpperCase())
                .join('');
        }

        return userName
            .slice(0, numChars)
            .toUpperCase();
    };

    const getBackgroundColor = () => {
        if (bgColor) {
            return `var(${bgColor})`;
        }

        if (!userName || userName === '?') return '#a5b4fc';

        const hash = userName.split('').reduce(
            (acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0
        );
        const hue = Math.abs(hash) % 360;
        return `hsl(${hue}, 60%, 60%)`;
    };

    const getTextColor = () => {
        if (textColor) {
            return `var(${textColor})`;
        }
        return 'var(--text-on-primary)';
    };

    const getFont = () => {
        if (font) {
            return `var(${font})`;
        }
        return undefined;
    };

    const renderAvatar = () => {
        if (imageUrl && !imageError) {
            return (
                <img
                    src={imageUrl}
                    alt="User Avatar"
                    className="user-avatar-image"
                    style={{
                        height: `${size}px`,
                        width: `${size}px`
                    }}
                    onError={handleImageError}
                />
            );
        }

        const style = {
            height: `${size}px`,
            width: `${size}px`,
            backgroundColor: getBackgroundColor(),
            color: getTextColor(),
        };

        if (!getFont()) {
            style.fontSize = `${size * 0.45}px`
        }

        if (getFont()) {
            style.font = getFont();
        }

        return (
            <div
                className="user-avatar-initials"
                style={style}
            >
                {getInitials()}
            </div>
        );
    };

    if (showNameOnHover && userName && userName !== '?') {
        return (
            <div
                ref={avatarRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={() => setHovered(false)}
            >
                {renderAvatar()}
                {hovered && createPortal(
                    <div
                        className="user-avatar-tooltip"
                        style={{ top: tooltipPos.top, left: tooltipPos.left }}
                    >
                        {userName}
                    </div>,
                    document.body
                )}
            </div>
        );
    }

    return renderAvatar();
};

export default UserAvatar;